import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import {
    getAllSurveys,
    getQuestionsForManagers,
    getQuestionsForParticipants,
    getSurveyById,
    getSurveyForParticipant,
    type Question,
    type Survey,
} from "./surveyApi";
import { getAllProductInstances, type ProductInstance } from "./productAPI";
import {
    getParticipantByTemporaryUuid,
    getParticipantCount,
    getParticipants,
    type Participant,
    type ParticipantCount,
    type SafeParticipant,
} from "./participantApi";
import { getPulseInstanceById, type PulseInstance } from "./pulseInstanceAPI";
import { getAnswersBySurveyId, type Answer } from "./answersAPI";
import { getDrivers, type Driver } from "./driverApi";
import { getTaxonomies, type Taxonomy } from "./taxonomyApi";
import {
    type Pagination,
    type Sorting,
    type User,
    getUserById,
    getUsers,
    getUsersByEmails,
    getUsersByIds,
} from "./userAPI";

function userByIdOptions({ id }: { id: number | null }) {
    return queryOptions<User, AxiosError>({
        queryKey: ["user", id],
        queryFn: async () => (id ? await getUserById(id) : ({} as User)),
        enabled: !!id,
        gcTime: 5000 * 60,
        staleTime: Infinity,
    });
}

function getUsersByEmailsOptions({ customerId, emails }: { customerId: number; emails: string[] }) {
    return queryOptions<User[], AxiosError>({
        queryKey: ["usersByEmails", customerId, emails],
        queryFn: async () => await getUsersByEmails(customerId, emails),
    });
}

function getUsersByIdsOptions({ ids }: { ids: number[] }) {
    return queryOptions<User[], AxiosError>({
        queryKey: ["usersByIds", ids],
        queryFn: async () => await getUsersByIds(ids),
    });
}

function usersInfiniteOptions({
    queries,
    take = 10,
    enabled = true,
}: {
    queries: Sorting;
    take?: number;
    enabled?: boolean;
}) {
    return infiniteQueryOptions({
        queryKey: ["usersInf", queries, take],
        queryFn: ({ pageParam }) => getUsers({ ...queries, ...pageParam }),
        initialPageParam: { skip: 0, take } as Pagination,
        getNextPageParam: (lastPage, _, lastPageParam) => {
            if (lastPage.data.length === 0) {
                return undefined;
            }
            return {
                skip: lastPageParam.skip + lastPageParam.take,
                take: lastPageParam.take,
            } as Pagination;
        },
        getPreviousPageParam: (_, __, firstPageParam) => {
            if (firstPageParam.skip === 0) {
                return undefined;
            }
            return {
                skip: Math.max(0, firstPageParam.skip - firstPageParam.take),
                take: firstPageParam.take,
            } as Pagination;
        },
        enabled,
    });
}

function getSurveysOptions({ pulseInstanceId }: { pulseInstanceId: number }) {
    return queryOptions<Survey[]>({
        queryKey: ["surveys", pulseInstanceId],
        queryFn: async (): Promise<Survey[]> => getAllSurveys({ pulseInstanceId }),
    });
}

function getSurveyOptions({ surveyId }: { surveyId: number }) {
    return queryOptions({
        queryKey: ["survey", surveyId],
        queryFn: async (): Promise<Survey> => getSurveyById({ surveyId }),
    });
}

function surveyForParticipantOptions({ surveyId }: { surveyId: number }) {
    return queryOptions({
        queryKey: ["surveyForParticipant", surveyId],
        queryFn: async (): Promise<Survey> => getSurveyForParticipant({ surveyId }),
    });
}

function QuestionsForParticipantsQueryOptions({ surveyId }: { surveyId: number }) {
    return queryOptions({
        queryKey: ["questionsForParticipant", surveyId],
        queryFn: async (): Promise<Question[]> => getQuestionsForParticipants({ surveyId }),
    });
}

function QuestionsForManagerQueryOptions({ surveyId }: { surveyId: number }) {
    return queryOptions({
        queryKey: ["questionsForManager", surveyId],
        queryFn: async (): Promise<Question[]> => getQuestionsForManagers({ surveyId }),
    });
}

function ProductInstanceQueryOptions() {
    return queryOptions<ProductInstance[]>({
        queryKey: ["productInstances"],
        queryFn: async (): Promise<ProductInstance[]> => getAllProductInstances(),
    });
}

function ParticipantQueryOptions({ participantId }: { participantId: string }) {
    return queryOptions({
        queryKey: ["participant", participantId],
        queryFn: async (): Promise<Participant> =>
            getParticipantByTemporaryUuid({ temporaryUuid: participantId }),
    });
}

function ParticipantCountQueryOptions({ surveyId }: { surveyId: number }) {
    return queryOptions({
        queryKey: ["participantCount", surveyId],
        queryFn: async (): Promise<ParticipantCount> => getParticipantCount({ surveyId }),
    });
}

function ParticipantsForSurveyQueryOptions({ surveyId }: { surveyId: number }) {
    return queryOptions({
        queryKey: ["participantsForSurvey", surveyId],
        queryFn: async (): Promise<SafeParticipant[]> => getParticipants({ surveyId }),
    });
}

function PulseInstanceQueryOptions({ pulseInstanceId }: { pulseInstanceId: number }) {
    return queryOptions({
        queryKey: ["pulseInstance", pulseInstanceId],
        queryFn: async (): Promise<PulseInstance> => getPulseInstanceById({ pulseInstanceId }),
    });
}

function answersBySurveyIdOptions({ surveyId }: { surveyId: number }) {
    return queryOptions({
        queryKey: ["answers", surveyId],
        queryFn: async (): Promise<Answer[]> => getAnswersBySurveyId({ surveyId }),
    });
}

function driversQueryOptions() {
    return queryOptions<Driver[]>({
        queryKey: ["drivers"],
        queryFn: getDrivers,
    });
}

function taxonomiesQueryOptions() {
    return queryOptions<Taxonomy[]>({
        queryKey: ["taxonomies"],
        queryFn: getTaxonomies,
    });
}

export {
    userByIdOptions,
    getSurveysOptions,
    getSurveyOptions,
    QuestionsForParticipantsQueryOptions,
    QuestionsForManagerQueryOptions,
    ProductInstanceQueryOptions,
    ParticipantQueryOptions,
    PulseInstanceQueryOptions,
    ParticipantCountQueryOptions,
    answersBySurveyIdOptions,
    getUsersByEmailsOptions,
    surveyForParticipantOptions,
    usersInfiniteOptions,
    ParticipantsForSurveyQueryOptions,
    getUsersByIdsOptions,
    driversQueryOptions,
    taxonomiesQueryOptions,
};
